import { ReactNode, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import {
  FileText,
  FileCheck,
  User,
  ArrowLeftFromLine,
  ArrowRightFromLine,
  Shield,
  Image as ImageIcon,
  Store,
  BarChart3,
} from 'lucide-react'
import { useCurrentUser } from '@/hooks/useCurrentUser'
import { useNotificationSSE } from '@/hooks/useNotificationSSE'
import { useTokenRefresh } from '@/hooks/useTokenRefresh'
import { useLogout } from '@/hooks/useLogin'
import { NotificationBell } from '@/components/Notifications/NotificationBell'
import { useSidebarStore } from '@/stores/useSidebarStore'
import { useMediaQuery } from '@/hooks/useMediaQuery'
import {
  Sidebar,
  SidebarContent,
  SidebarNav,
  type SidebarNavItem,
} from '@/components/ui/sidebar-nav'
import { Separator } from '@/components/ui/separator'
import { Toaster } from '@/components/common/Toaster'

interface LayoutProps {
  children: ReactNode
}

export default function Layout({ children }: LayoutProps) {
  const navigate = useNavigate()
  const location = useLocation()
  const { logout } = useLogout()
  const { isCollapsed, isAutoCollapsed, toggle, setAutoCollapsed } =
    useSidebarStore()
  const { isAdmin, isManager, name } = useCurrentUser()
  const isDesktop = useMediaQuery('(min-width: 1280px)')
  useNotificationSSE()
  useTokenRefresh()

  useEffect(() => {
    setAutoCollapsed(!isDesktop)
  }, [isDesktop, setAutoCollapsed])

  const effectiveCollapsed = isCollapsed || isAutoCollapsed

  const navItems: SidebarNavItem[] = [
    {
      id: 'assortment-plans',
      label: 'Assortment Plans',
      icon: <FileText className="h-4 w-4" />,
      href: '/',
    },
    {
      id: 'dashboard',
      label: 'Dashboard',
      icon: <BarChart3 className="h-4 w-4" />,
      children: [
        { id: 'dashboard-cat', label: 'CAT', href: '/dashboard/cat' },
        { id: 'dashboard-ifp', label: 'Flow Tool', href: '/dashboard/ifp' },
      ],
    },
    ...(isManager
      ? [
          {
            id: 'approvals',
            label: 'Approvals',
            icon: <FileCheck className="h-4 w-4" />,
            href: '/approvals',
          },
        ]
      : []),
    {
      id: 'images',
      label: 'Images',
      icon: <ImageIcon className="h-4 w-4" />,
      href: '/images',
    },
    {
      id: 'store-models',
      label: 'Store Models',
      icon: <Store className="h-4 w-4" />,
      href: '/store-models',
    },
    ...(isAdmin
      ? [
          {
            id: 'admin',
            label: 'Admin',
            icon: <Shield className="h-4 w-4" />,
            href: '/admin',
          },
        ]
      : []),
  ]

  const getActiveId = () => {
    if (location.pathname.startsWith('/approvals')) return 'approvals'
    if (location.pathname.startsWith('/images')) return 'images'
    if (location.pathname.startsWith('/store-models')) return 'store-models'
    if (location.pathname.startsWith('/admin')) return 'admin'
    if (location.pathname.startsWith('/dashboard/ifp')) return 'dashboard-ifp'
    if (location.pathname.startsWith('/dashboard')) return 'dashboard-cat'
    if (location.pathname === '/' || location.pathname.startsWith('/plans/'))
      return 'assortment-plans'
    return undefined
  }

  const handleLogout = () => {
    logout()
  }

  return (
    <div className="flex h-screen flex-col overflow-hidden bg-background">
      {/* Full-width Top Nav */}
      <header className="flex h-14 items-center justify-between border-b border-border bg-card px-6">
        {/* Left side - Logo and branding */}
        <div className="flex items-center gap-6">
          <img src="/logo.svg" alt="logo" className="h-9 w-auto" />
          <Separator orientation="vertical" className="h-6" />
          <span className="text-base font-medium text-muted-foreground">
            Category Assortment Tool
          </span>
        </div>

        {/* Right side - Help and user info */}
        <div className="flex items-center gap-4">
          <NotificationBell />
          <Separator orientation="vertical" className="h-6" />
          <div className="flex items-center gap-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-muted">
              <User className="h-4 w-4 text-muted-foreground" />
            </div>
            <div className="text-sm">
              <p className="font-medium text-foreground">{name ?? 'User'}</p>
              <button
                onClick={handleLogout}
                className="text-xs text-muted-foreground hover:text-primary cursor-pointer"
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      </header>
      {/* Sidebar + Content row */}
      <div className="flex flex-1 overflow-hidden">
        <Sidebar isCollapsed={effectiveCollapsed}>
          {/* Collapse/Expand toggle button */}
          <div
            className={`flex p-2 ${effectiveCollapsed ? 'justify-center' : 'justify-end'}`}
          >
            <button
              onClick={toggle}
              className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-md text-muted-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground transition-colors"
              title={effectiveCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            >
              {effectiveCollapsed ? (
                <ArrowRightFromLine className="h-4 w-4" />
              ) : (
                <ArrowLeftFromLine className="h-4 w-4" />
              )}
            </button>
          </div>
          <SidebarContent>
            <SidebarNav
              items={navItems}
              activeId={getActiveId()}
              onItemClick={item => item.href && navigate(item.href)}
              isCollapsed={effectiveCollapsed}
            />
          </SidebarContent>
        </Sidebar>

        <main className="flex-1 overflow-auto bg-background">{children}</main>
      </div>
      <Toaster />
    </div>
  )
}
